import type { GridApi, IRowNode } from 'ag-grid-community'

type RecordValue = Record<string, unknown>

const isRecord = (value: unknown): value is RecordValue =>
  typeof value === 'object' && value !== null

type RowWithId = { id?: string }

export const isNewRowId = (id: string | undefined): boolean => {
  return typeof id === 'string' && id.startsWith('new-')
}

export const hasRowId = (row: unknown): row is RowWithId => {
  if (!isRecord(row)) return false
  return 'id' in row
}

type RowWithDraft = { draft: boolean }

export const hasDraftFlag = (row: unknown): row is RowWithDraft => {
  if (!isRecord(row)) return false
  return typeof row.draft === 'boolean'
}

type RevealRowByIdOptions = {
  column?: string
  maxRetries?: number
  retryDelayMs?: number
  select?: boolean
  flash?: boolean
}

type GridApiWithDestroyed = {
  isDestroyed?: () => boolean
}

const isApiDestroyed = <Row,>(api: GridApi<Row>): boolean => {
  const maybeApi = api as GridApiWithDestroyed
  return typeof maybeApi.isDestroyed === 'function' ? maybeApi.isDestroyed() : false
}

const findRowNodeById = <Row extends RowWithId>(
  api: GridApi<Row>,
  id: string,
): IRowNode<Row> | undefined => {
  const rowNode = api.getRowNode(id)
  if (rowNode) return rowNode

  let matchedNode: IRowNode<Row> | undefined
  api.forEachNode((candidate) => {
    if (matchedNode) return
    if (candidate.data?.id === id) {
      matchedNode = candidate
    }
  })
  return matchedNode
}

export const revealRowById = <Row extends RowWithId>(
  api: GridApi<Row>,
  id: string,
  options: RevealRowByIdOptions = {},
): void => {
  const {
    column,
    maxRetries = 10,
    retryDelayMs = 50,
    select = true,
    flash = true,
  } = options

  const revealNow = (): boolean => {
    if (isApiDestroyed(api)) return true

    const rowNode = findRowNodeById(api, id)
    if (!rowNode) return false

    try {
      if (select) {
        api.deselectAll()
        rowNode.setSelected(true)
      }
      api.ensureNodeVisible(rowNode)
      if (flash) {
        api.flashCells({
          rowNodes: [rowNode],
          ...(column ? { columns: [column] } : {}),
        })
      }
      return true
    } catch {
      return false
    }
  }

  if (revealNow()) return

  let attemptsLeft = maxRetries
  const retry = () => {
    if (attemptsLeft <= 0) return
    attemptsLeft -= 1
    setTimeout(() => {
      if (!revealNow()) retry()
    }, retryDelayMs)
  }

  retry()
}
